import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  BeneficiaryType,
  UpdateBeneficiaryType
} from 'apps/user/types/on-boarding.type';
import { AxiosError } from 'axios';
import { Alert } from 'components/Toast';
import { toast } from 'react-toastify';
import { services } from 'service';
import { BeneficiaryDto } from 'service/dto/beneficiary.dto';

interface IProps {
  update: UpdateBeneficiaryType;
  beneficiaryId: number;
}

export const useCreateBeneficiary = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (beneficiaryType: BeneficiaryType): Promise<void> => {
      return services.beneficiaryService.createBeneficiary(beneficiaryType);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['create-beneficiary']);
      },
      onError: (error: Error) => {
        toast.error(<Alert message={error.message} type="error" />);
      }
    }
  );
};

export const useUpdateBeneficiary = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ beneficiaryId, update }: IProps): Promise<void> => {
      return services.beneficiaryService.updateBeneficiary(
        beneficiaryId,
        update
      );
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['update-beneficiary']);
      },
      onError: (error: Error) => {
        toast.error(<Alert message={error.message} type="error" />);
      }
    }
  );
};

export const useGetBeneficiaryList = ({
  enabled = true,
  search = undefined
}: {
  search?: string;
  enabled?: boolean;
} = {}) => {
  const searchQuery = search && search?.length >= 3 ? search : '';
  return useQuery(
    ['beneficiary'],
    () => services.beneficiaryService.getBeneficiaries(searchQuery),
    {
      refetchOnWindowFocus: false,
      enabled: enabled
    }
  );
};

export const useDeleteBeneficiary = () =>
  useMutation(
    (beneficiaryId: number) =>
      services.beneficiaryService.deleteBeneficiary(beneficiaryId),
    {
      onError: (error: Error) => {
        toast.error(<Alert message={error.message} type="error" />);
      }
    }
  );

export const useGetBeneficiary = ({
  beneficiaryId
}: {
  beneficiaryId: number;
}) => {
  return useQuery<BeneficiaryDto, AxiosError<Error>>(
    ['get_beneficiary', beneficiaryId],
    () => services.beneficiaryService.getBeneficiary(beneficiaryId),
    {
      keepPreviousData: false,
      refetchOnWindowFocus: false,
      enabled: !!beneficiaryId
    }
  );
};

export const useGetBeneficiaryDetails = ({
  beneficiaryId
}: {
  beneficiaryId: number;
}) => {
  return useQuery<BeneficiaryDto, AxiosError<Error>>(
    ['get_beneficiary_details', beneficiaryId],
    () => services.beneficiaryService.getBeneficiaryDetails(beneficiaryId),
    {
      keepPreviousData: false,
      refetchOnWindowFocus: false,
      enabled: !!beneficiaryId
    }
  );
};
